import { Checkbox } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Reaptcha from 'reaptcha';
import { Spin } from 'antd';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import useStyle from './index.styles';
import { ReactComponent as GoogleCaptchaIcon } from '../../assets/images/googleCaptcha.svg';

const CaptchaMemorial = ({
  captchaToken,
  disabled,
  showRequiredError,
  setCaptchaToken,
}: {
  captchaToken: string;
  disabled?: boolean;
  showRequiredError: boolean;
  setCaptchaToken: (token: string) => void;
}) => {
  const [isGeneratingToken, setIsGeneratingToken] = useState<boolean>(false);
  const [showExpiredTokenMessage, setShowExpiredTokenMessage] = useState<boolean>(false);
  const [showErrorValidatingCaptcha, setShowErrorValidatingCaptcha] = useState<boolean>(false);
  const captchaRef = useRef<Reaptcha>(null);
  const classes = useStyle();
  const { t } = useTranslation();

  useEffect(() => {
    if (isGeneratingToken && captchaToken) {
      setIsGeneratingToken(false);
    }
  }, [captchaToken, isGeneratingToken]);

  useEffect(() => {
    if (showExpiredTokenMessage && captchaToken) {
      setShowExpiredTokenMessage(false);
    }
  }, [captchaToken, showExpiredTokenMessage]);

  useEffect(() => {
    if (showErrorValidatingCaptcha && captchaToken) {
      setShowErrorValidatingCaptcha(false);
    }
  }, [captchaToken, showErrorValidatingCaptcha]);

  useEffect(() => {
    // Clean captcha when it was validated in the api and action return some error
    if (!captchaToken && captchaRef.current?.state.rendered) {
      captchaRef.current.reset();
    }
  }, [captchaToken]);

  return (
    <>
      <div className="relative flex">
        {isGeneratingToken && (
          <Spin
            indicator={<Loading3QuartersOutlined className="text-[24px]" size={24} spin />}
            className="absolute"
          />
        )}
        <Checkbox
          onChange={
            !captchaToken
              ? () => {
                  setIsGeneratingToken(true);
                  captchaRef.current?.execute();
                }
              : undefined
          }
          disabled={disabled || isGeneratingToken}
          checked={!!captchaToken}
          className={`${isGeneratingToken ? classes.hideCheckbox : ''}`}
        >
          {t('captchaMemorial.notARobot')}
        </Checkbox>
        <GoogleCaptchaIcon />
        <Reaptcha
          ref={captchaRef}
          sitekey={process.env.REACT_APP_SITE_KEY || ''}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={() => {
            setCaptchaToken('');
            setShowExpiredTokenMessage(true);
          }}
          onError={() => {
            setCaptchaToken('');
            setShowErrorValidatingCaptcha(true);
          }}
          size="invisible"
        />
      </div>
      <p className="smallParagraph h-[20px] text-danger">
        {!isGeneratingToken &&
          !disabled &&
          (showRequiredError || showErrorValidatingCaptcha || showExpiredTokenMessage) && (
            <>
              {showRequiredError
                ? t('captchaMemorial.requiredError')
                : showErrorValidatingCaptcha
                  ? t('captchaMemorial.errorValidatingCaptcha')
                  : t('captchaMemorial.expiredToken')}
            </>
          )}
      </p>
    </>
  );
};

export default CaptchaMemorial;
